import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { HOME } from '@/data/home';
import { JDI, jdiRow, stateLabel, type Health } from '@/data/jdi';
import { Reveal } from '@components/motion';
import FigmaFrame from './FigmaFrame';
import MAP_SVG from './nigeria-map.svg?raw';

const SKYLINE = '/landing/justice-performance/skyline.webp';
const ARROW = '/landing/justice-performance/arrow.svg';

/**
 * State name for each <path> in nigeria-map.svg, by document order. Derived by
 * geometric registration of this map against a labelled reference map of
 * Nigeria (per-path vertex centroids, globally matched). `null` entries are
 * tiny island fragments that are not clickable on their own.
 */
const STATE_BY_PATH: ReadonlyArray<string | null> = [
    'Borno',
    'Niger',
    'Taraba',
    'Bauchi',
    'Yobe',
    'Kaduna',
    'Kebbi',
    'Zamfara',
    'Kwara',
    'Adamawa',
    'Sokoto',
    'Benue',
    'Kogi',
    'Plateau',
    'Oyo',
    'Nasarawa',
    'Katsina',
    'Jigawa',
    'Cross River',
    'Kano',
    'Edo',
    'Gombe',
    'Ogun',
    'Delta',
    'Ondo',
    'Bayelsa',
    'Osun',
    'Enugu',
    'FCT Abuja',
    'Rivers',
    'Akwa Ibom',
    'Ebonyi',
    'Imo',
    'Ekiti',
    'Anambra',
    'Abia',
    'Lagos',
    null,
    null,
    null,
    null,
];

/** Display label for a state ("Ogun State", but "FCT Abuja" stays as-is). */
const HEALTH_STYLES: Record<Health, { color: string; bg: string }> = {
    Good: { color: '#28c76f', bg: 'rgba(40,199,111,0.16)' },
    Fair: { color: '#ff9f43', bg: 'rgba(255,159,67,0.16)' },
    Poor: { color: '#ea5455', bg: 'rgba(234,84,85,0.16)' },
};

/** Path data extracted once from the raw SVG, paired with its state name. */
const MAP_PATHS: ReadonlyArray<{ d: string; state: string | null }> = [...MAP_SVG.matchAll(/<path d="([^"]+)"/g)].map(
    (m, i) => ({ d: m[1], state: STATE_BY_PATH[i] ?? null }),
);

/**
 * Average of a path's on-curve endpoints — a good-enough visual centre for
 * anchoring the connector. The map's path data uses absolute commands only.
 */
function pathCentroid(d: string): { x: number; y: number } {
    const tokens = d.match(/[A-Za-z]|-?\d*\.?\d+/g) ?? [];
    let i = 0;
    let cmd = '';
    let x = 0;
    let y = 0;
    let sumX = 0;
    let sumY = 0;
    let count = 0;
    const num = () => parseFloat(tokens[i++]);
    const record = () => {
        sumX += x;
        sumY += y;
        count++;
    };
    while (i < tokens.length) {
        if (/[A-Za-z]/.test(tokens[i])) {
            cmd = tokens[i++].toUpperCase();
            continue;
        }
        switch (cmd) {
            case 'M':
            case 'L':
            case 'T':
                x = num();
                y = num();
                record();
                break;
            case 'H':
                x = num();
                record();
                break;
            case 'V':
                y = num();
                record();
                break;
            case 'C':
                num();
                num();
                num();
                num();
                x = num();
                y = num();
                record();
                break;
            case 'S':
            case 'Q':
                num();
                num();
                x = num();
                y = num();
                record();
                break;
            default:
                i++;
                break;
        }
    }
    return count ? { x: sumX / count, y: sumY / count } : { x: 0, y: 0 };
}

/** Visual centre of each state, in the map's 586×487 viewBox space. */
const STATE_CENTROIDS: Record<string, { x: number; y: number }> = {};
for (const p of MAP_PATHS) {
    if (p.state && !(p.state in STATE_CENTROIDS)) STATE_CENTROIDS[p.state] = pathCentroid(p.d);
}

/* Map placement within the 1920×1101 desktop frame (matches the map Reveal). */
const MAP_LEFT = 735;
const MAP_TOP = 145;
/* Left edge of the breakdown card the connector runs to. */
const CARD_LEFT = 1421;

/**
 * Connector from the selected state's centre to the breakdown card. Keyed by
 * state where it's used, so every click remounts it and replays the dot-pop +
 * line-draw animations.
 */
function ConnectorLine({ state }: { state: string }) {
    const c = STATE_CENTROIDS[state];
    if (!c) return null;
    const sx = MAP_LEFT + c.x;
    const sy = MAP_TOP + c.y;
    // Run right from the state, then jog vertically so we always meet the card
    // within its face, then finish at its left edge.
    const targetY = Math.min(760, Math.max(240, sy));
    const d = `M ${sx.toFixed(1)} ${sy.toFixed(1)} H 1385 V ${targetY.toFixed(1)} H ${CARD_LEFT}`;
    return (
        <svg viewBox="0 0 1920 1101" fill="none" aria-hidden className="pointer-events-none absolute inset-0 size-full">
            <circle cx={sx} cy={sy} r="5.5" fill="#f60" className="cg-conn-dot" />
            <path d={d} stroke="#f60" strokeWidth="2" pathLength={1} className="cg-conn-line" />
        </svg>
    );
}

/**
 * Interactive Nigeria map, rendered as real React <path> elements so selection
 * and click handling are ordinary props (immune to re-render races). The
 * selected state is filled dark; the rest are light grey.
 */
function NigeriaMap({
    selected,
    onSelect,
    className,
}: {
    selected: string;
    onSelect: (state: string) => void;
    className?: string;
}) {
    return (
        <svg
            viewBox="0 0 586 487"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`cg-ng-map ${className ?? ''}`}
            role="img"
            aria-label="Interactive map of Nigeria — click a state to see its justice performance"
        >
            {MAP_PATHS.map((p, i) =>
                p.state ? (
                    <path
                        key={i}
                        d={p.d}
                        data-state={p.state}
                        data-selected={p.state === selected ? 'true' : 'false'}
                        fill={p.state === selected ? '#1b1b1b' : '#e1e1e1'}
                        onClick={() => onSelect(p.state!)}
                    >
                        <title>{stateLabel(p.state)}</title>
                    </path>
                ) : (
                    <path key={i} d={p.d} fill="#e1e1e1" pointerEvents="none" />
                ),
            )}
        </svg>
    );
}

/* ---------------- Shared content pieces ---------------- */

function PerformanceCardBody({ state, year }: { state: string; year: string }) {
    const row = jdiRow(year, state);
    const health = row?.health ? HEALTH_STYLES[row.health] : null;
    const days = (n: number | null | undefined) =>
        n === null || n === undefined ? 'Not published' : `${Math.round(n).toLocaleString('en-NG')} Days`;
    return (
        <>
            {/* DETAILS label */}
            <p className="text-[13px] font-normal leading-[20px] text-[#93909d]">DETAILS</p>

            {/* List */}
            <div className="mt-[16px] flex flex-col gap-[12px]">
                {row ? (
                    <>
                        <p className="text-[14px] leading-[24px] text-[#34323c]">
                            Civil Case:&nbsp;&nbsp;
                            <span className="font-bold text-[#1b1b1b]">{days(row.civilDays)}</span>
                        </p>
                        <p className="text-[14px] leading-[24px] text-[#34323c]">
                            Criminal Case:&nbsp;&nbsp;<span className="font-bold">{days(row.criminalDays)}</span>
                        </p>
                        <div className="flex items-center gap-[12px]">
                            <span className="text-[14px] leading-[24px] text-[#34323c]">Justice Health:</span>
                            {health && row.health ? (
                                <span
                                    className="rounded-[4px] px-[10px] py-[5px] text-[13px] font-medium leading-[14px]"
                                    style={{ color: health.color, backgroundColor: health.bg }}
                                >
                                    {row.health}
                                </span>
                            ) : (
                                <span className="rounded-[4px] bg-[#f0f0f0] px-[10px] py-[5px] text-[13px] font-medium leading-[14px] text-[#5e5e5e]">
                                    Withheld
                                </span>
                            )}
                        </div>
                        {row.rank !== null && (
                            <p className="text-[14px] leading-[24px] text-[#34323c]">
                                Rank:&nbsp;&nbsp;<span className="font-bold text-[#1b1b1b]">{row.rank}</span>
                            </p>
                        )}
                        <p className="text-[14px] leading-[24px] text-[#34323c]">{row.description}</p>
                    </>
                ) : (
                    <p className="text-[14px] leading-[24px] text-[#34323c]">
                        No figures were published for {stateLabel(state)} in {year}.
                    </p>
                )}
            </div>
        </>
    );
}

/** The CMS's call to action under the heading — only when it has been given a label. */
function RankingCta({ className }: { className?: string }) {
    const { ctaLabel, ctaHref } = HOME.justicePerformance;
    if (!ctaLabel.trim()) return null;
    return (
        <Link
            to={ctaHref || '/resources/court-ranking'}
            className={`inline-flex items-center justify-center rounded-[10px] bg-[#1b1b1b] px-8 py-4 text-[16px] font-bold text-white transition-transform duration-300 hover:scale-[1.03] ${className ?? ''}`}
        >
            {ctaLabel}
        </Link>
    );
}

function CourtRankingsHeading({ className }: { className?: string }) {
    return (
        <p className={className}>
            <span className="text-[#1b1b1b]">{HOME.justicePerformance.titleLead}</span>
            <span className="font-normal text-[#6c6c6c]">{HOME.justicePerformance.titleBold}</span>
        </p>
    );
}

/* ---------------- Desktop (Figma-faithful) ---------------- */

function JusticePerformanceDesktop() {
    const yearsRef = useRef<HTMLDivElement>(null);
    const [selectedState, setSelectedState] = useState('Ogun');
    const [selectedYear, setSelectedYear] = useState(JDI.year);

    const scrollYears = (dir: number) => {
        yearsRef.current?.scrollBy({ left: dir * 260, behavior: 'smooth' });
    };

    return (
        <FigmaFrame designWidth={1920} designHeight={1101} className="w-full">
            <div className="relative h-[1101px] w-[1920px] bg-[#f9f9f9] font-['Plus_Jakarta_Sans',sans-serif]">
                {/* Intro paragraph */}
                <Reveal
                    as="p"
                    once
                    variant="fade-up"
                    className="absolute left-[84px] top-[159px] w-[420px] text-[16px] font-normal leading-[24px] text-[#333]"
                >
                    {HOME.justicePerformance.blurb}
                </Reveal>

                {/* The Court Rankings heading */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={120}
                    className="absolute left-[84px] top-[365px] w-[542px]"
                >
                    <CourtRankingsHeading className="text-[96px] font-bold leading-[103px]" />
                    <RankingCta className="mt-[36px]" />
                </Reveal>

                {/* Nigeria map — click a state to update the breakdown card */}
                <Reveal
                    as="div"
                    once
                    variant="scale"
                    duration={1100}
                    className="absolute left-[735px] top-[145px] h-[487px] w-[585px]"
                >
                    <NigeriaMap selected={selectedState} onSelect={setSelectedState} className="size-full" />
                </Reveal>

                {/* Selected state name — centred on the connector line's horizontal run */}
                <p
                    key={`label-${selectedState}`}
                    className="animate-fadeIn absolute -translate-x-1/2 -translate-y-full whitespace-nowrap text-[28px] font-bold leading-[36px] text-[#1b1b1b]"
                    style={{
                        left: (MAP_LEFT + (STATE_CENTROIDS[selectedState]?.x ?? 0) + 1385) / 2,
                        top: MAP_TOP + (STATE_CENTROIDS[selectedState]?.y ?? 0) - 10,
                    }}
                >
                    {selectedState}
                </p>

                {/* Subtle usage hint */}
                <p className="absolute left-[735px] top-[648px] text-[13px] font-normal italic leading-[20px] text-[#9a9a9a]">
                    Click any state on the map to see its justice performance.
                </p>

                {/* Connector — redraws from the selected state on every click */}
                <ConnectorLine key={`conn-${selectedState}`} state={selectedState} />

                {/* Justice Performance Breakdown card */}
                <Reveal
                    as="div"
                    once
                    variant="fade-left"
                    delay={300}
                    className="absolute left-[1421px] top-[198px] h-[603px] w-[360px] rounded-[6px] border border-[#dadbdd] bg-[#f9f9f9]"
                >
                    <p className="mt-[24px] text-center text-[20px] font-bold leading-[32px] text-[#1b1b1b]">
                        Justice Performance Breakdown
                    </p>
                    <p className="mt-[8px] pl-[23px] text-[15px] font-semibold leading-[22px] text-[#93909d] [font-family:'Montserrat',sans-serif]">
                        {stateLabel(selectedState)} — {selectedYear}
                    </p>
                    <div className="mt-[17px] h-px w-full bg-[#dbdade]" />
                    <div className="scrollbar-hide h-[456px] overflow-y-auto px-[23px] pt-[10px]">
                        <PerformanceCardBody state={selectedState} year={selectedYear} />
                    </div>
                </Reveal>

                {/* Year Of Research selector */}
                <Reveal
                    as="div"
                    once
                    variant="fade-up"
                    delay={400}
                    className="absolute left-[600px] top-[726px] w-[720px]"
                >
                    <p className="text-[18px] font-bold leading-[28px] text-[#1b1b1b]">Year Of Research</p>
                    <div className="mt-[9px] flex h-[65px] items-center gap-[30px]">
                        {/* Left chevron */}
                        <button
                            type="button"
                            aria-label="Earlier years"
                            onClick={() => scrollYears(-1)}
                            className="flex size-[41px] shrink-0 items-center justify-center"
                        >
                            <img src={ARROW} alt="" className="h-[11px] w-[20px] -rotate-90" />
                        </button>

                        {/* Horizontally scrollable years */}
                        <div
                            ref={yearsRef}
                            className="scrollbar-hide flex h-[65px] grow items-center gap-[26px] overflow-x-auto scroll-smooth"
                        >
                            {JDI.years.map((year) => (
                                <button
                                    key={year}
                                    type="button"
                                    onClick={() => setSelectedYear(year)}
                                    aria-pressed={year === selectedYear}
                                    className={`flex shrink-0 items-center justify-center rounded-[8px] px-[16px] py-[8px] transition-colors duration-300 ${
                                        year === selectedYear ? 'bg-[#e9e9e9]' : 'hover:bg-[#f0f0f0]'
                                    }`}
                                >
                                    <span
                                        className={`text-[32px] leading-[40px] tracking-[-0.5px] text-[#1b1b1b] ${
                                            year === selectedYear ? 'font-bold' : 'font-normal'
                                        }`}
                                    >
                                        {year}
                                    </span>
                                </button>
                            ))}
                        </div>

                        {/* Right chevron */}
                        <button
                            type="button"
                            aria-label="Later years"
                            onClick={() => scrollYears(1)}
                            className="flex size-[41px] shrink-0 items-center justify-center"
                        >
                            <img src={ARROW} alt="" className="h-[11px] w-[20px] rotate-90" />
                        </button>
                    </div>
                </Reveal>

                {/* Skyline silhouette */}
                <img src={SKYLINE} alt="" className="absolute bottom-0 left-[97px] h-[144px] w-[1725px] max-w-none" />
            </div>
        </FigmaFrame>
    );
}

/* ---------------- Mobile (stacked) ---------------- */

function JusticePerformanceMobile() {
    const yearsRef = useRef<HTMLDivElement>(null);
    const [selectedState, setSelectedState] = useState('Ogun');
    const [selectedYear, setSelectedYear] = useState(JDI.year);

    const scrollYears = (dir: number) => {
        yearsRef.current?.scrollBy({ left: dir * 160, behavior: 'smooth' });
    };

    return (
        <section className="relative overflow-hidden bg-white font-['Plus_Jakarta_Sans',sans-serif]">
            <div className="px-[21px] pb-[80px] pt-[34px]">
                {/* Heading first, per mobile frame */}
                <CourtRankingsHeading className="w-[206px] text-[40px] font-extrabold leading-[50px]" />

                {/* Intro paragraph */}
                <p className="mt-[24px] w-[334px] max-w-full text-[14px] font-normal leading-[24px] text-[#333]">
                    {HOME.justicePerformance.blurb}
                </p>
                <RankingCta className="mt-[24px]" />

                {/* Nigeria map */}
                <NigeriaMap selected={selectedState} onSelect={setSelectedState} className="mt-[40px] w-full" />

                {/* Subtle usage hint */}
                <p className="mt-[10px] text-[12px] font-normal italic leading-[18px] text-[#9a9a9a]">
                    Tap any state on the map to see its justice performance.
                </p>

                {/* Year Of Research — sits directly under the map instruction */}
                <div className="mt-[22px] w-[335px] max-w-full">
                    <p className="text-[14px] font-bold leading-[28px] text-[#1b1b1b]">Year Of Research</p>

                    <div className="mt-[8px] flex items-center gap-[10px]">
                        <button
                            type="button"
                            aria-label="Earlier years"
                            onClick={() => scrollYears(-1)}
                            className="flex size-[29px] shrink-0 items-center justify-center"
                        >
                            <img src={ARROW} alt="" className="h-[10px] w-[18px] -rotate-90" />
                        </button>

                        <div
                            ref={yearsRef}
                            className="scrollbar-hide flex h-[43px] grow items-center gap-[12px] overflow-x-auto scroll-smooth"
                        >
                            {JDI.years.map((year) => (
                                <button
                                    key={year}
                                    type="button"
                                    onClick={() => setSelectedYear(year)}
                                    aria-pressed={year === selectedYear}
                                    className={`flex shrink-0 items-center justify-center rounded-[8px] px-[8px] py-[4px] ${
                                        year === selectedYear ? 'bg-[#e9e9e9]' : ''
                                    }`}
                                >
                                    <span
                                        className={`text-[16px] leading-[40px] tracking-[-0.5px] text-[#1b1b1b] ${
                                            year === selectedYear ? 'font-bold' : 'font-normal'
                                        }`}
                                    >
                                        {year}
                                    </span>
                                </button>
                            ))}
                        </div>

                        <button
                            type="button"
                            aria-label="Later years"
                            onClick={() => scrollYears(1)}
                            className="flex size-[29px] shrink-0 items-center justify-center"
                        >
                            <img src={ARROW} alt="" className="h-[10px] w-[18px] rotate-90" />
                        </button>
                    </div>
                </div>

                {/* Selected state label */}
                <p
                    key={selectedState}
                    className="animate-fadeIn mt-[22px] text-[16px] font-bold leading-[28px] text-[#1b1b1b]"
                >
                    {stateLabel(selectedState)}
                </p>

                {/* Justice Performance Breakdown card */}
                <div className="mt-[24px] rounded-[6px] border border-[#dadbdd] bg-[#f9f9f9] p-[23px]">
                    <p className="text-center text-[20px] font-bold leading-[32px] text-[#1b1b1b]">
                        Justice Performance Breakdown
                    </p>
                    <p className="mt-[8px] text-[15px] font-semibold leading-[22px] text-[#93909d] [font-family:'Montserrat',sans-serif]">
                        {stateLabel(selectedState)} — {selectedYear}
                    </p>
                    <div className="my-[16px] h-px w-full bg-[#dbdade]" />
                    <PerformanceCardBody state={selectedState} year={selectedYear} />
                </div>
            </div>

            {/* Skyline silhouette */}
            <img src={SKYLINE} alt="" className="absolute bottom-0 left-0 h-[59px] w-full max-w-none object-cover" />
        </section>
    );
}

export default function JusticePerformance() {
    return (
        <>
            <div className="hidden lg:block">
                <JusticePerformanceDesktop />
            </div>
            <div className="lg:hidden">
                <JusticePerformanceMobile />
            </div>
        </>
    );
}
